import { Tag } from "../components.js";
import type { RegisteredScenarioLeverId, ScenarioAssumptionBasis } from "../api/scenarioService.js";

/**
 * NX-2633 (E8.8) — mode-aware lever definitions for the what-if simulator (AC1/AC2/AC3), resolved
 * via a strategy map keyed by `DeliveryMode` (AC5's SOLID OCP/LSP clause) — never an if/switch
 * chain. This is the SAME registry pattern the codebase already uses twice for the identical
 * per-mode dispatch problem: `DEFAULT_DELIVERY_HEALTH_STRATEGIES` (delivery-health.ts) and
 * `DEFAULT_OPENING_STATEMENT_STRATEGIES` (packages/ai/src/narrative.ts). A fourth delivery mode
 * would be one more `.set()` here, never an edit to `WhatIfSimulatorPanel.tsx`.
 *
 * The date-driven lever calls the inverse forecast through the scenario route. Other modes retain
 * their existing simulation controls.
 */

export type DeliveryMode = "scope-driven" | "date-driven" | "continuous";

/** One lever a mode can offer. `leverIds` names REGISTERED backend levers to POST when engaged; an
 *  empty array + a set `gapReason` is the honestly-verified "no live endpoint yet" case above. */
export interface LeverDef {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly leverIds: readonly RegisteredScenarioLeverId[];
  readonly gapReason?: string;
}

const SCOPE_DRIVEN_LEVERS: readonly LeverDef[] = [
  {
    id: "add-capacity",
    label: "Add capacity — move the forecast date",
    description:
      "Applies the throughput-adjustment lever (an own-data or cohort-benchmark capacity increase) and re-runs the forecast; the ribbon redraws to the new p50/p85 completion band.",
    leverIds: ["throughput-adjustment"],
  },
  {
    id: "recover-friction",
    label: "Recover lost capacity — move the forecast date",
    description:
      "Applies the friction-reduction lever (recovers a share of this project's own measured CI/review/dependency-wait cost) and re-runs the forecast.",
    leverIds: ["friction-reduction"],
  },
];

const DATE_DRIVEN_LEVERS: readonly LeverDef[] = [
  {
    id: "trade-scope-at-date",
    label: "Trade scope at the target date",
    description:
      "Compare aggregate scope by the declared date with a simulated scope/date trade. With no selection, one fewer item is modeled; no item-level outcome is claimed without a declared backlog order.",
    leverIds: ["trade-scope-at-date"],
  },
];

const CONTINUOUS_LEVERS: readonly LeverDef[] = [
  {
    id: "tighten-wip-limit",
    label: "Tighten WIP limits — stabilize flow",
    description:
      "Applies the wip-limit-tightening simulation lever (a declared own-data or cohort-benchmark throughput-stability uplift) and re-runs the forecast; assumptions stay explicitly marked as simulated.",
    leverIds: ["wip-limit-tightening"],
  },
];

export const WHAT_IF_LEVER_STRATEGIES: ReadonlyMap<DeliveryMode, readonly LeverDef[]> = new Map([
  ["scope-driven", SCOPE_DRIVEN_LEVERS],
  ["date-driven", DATE_DRIVEN_LEVERS],
  ["continuous", CONTINUOUS_LEVERS],
]);

/** Mode is honest N/A (no profile declared) => no levers, never a default guess — mirrors
 *  project-profile.ts's own `NA_PROFILE` honesty contract. */
export function leversForMode(mode: DeliveryMode | null): readonly LeverDef[] {
  return mode === null ? [] : (WHAT_IF_LEVER_STRATEGIES.get(mode) ?? []);
}

const BASIS_META: Record<ScenarioAssumptionBasis, { label: string; tone: "green" | "amber" }> = {
  "own-data": { label: "Own data", tone: "green" },
  "cohort-benchmark": { label: "Cohort benchmark", tone: "amber" },
};

/**
 * `basis: "own-data"` and `basis: "cohort-benchmark"` are NOT the same confidence and must not
 * render identically — the whole point of `ScenarioAssumption.basis` (scenario-engine.ts's own
 * doc comment). Distinct label text AND distinct tone (colour is never the only signal), so a test
 * can assert the DOM genuinely differs between the two, not merely that some basis rendered.
 */
export function AssumptionBasisTag({ basis }: { basis: ScenarioAssumptionBasis }): React.ReactNode {
  const meta = BASIS_META[basis];
  return <Tag tone={meta.tone}>{meta.label}</Tag>;
}
